/**
 * AI flags → scrubber markers.
 *
 * The AI review hands us two differently-shaped sources of timed findings:
 * `timestamped_issues[]` (a real object with a `timestamp` field) and
 * `risk_flags[]` (free-form strings that happen to carry an inline
 * `"High @ 00:01: …"` prefix). Both are LLM output, typed only by what we
 * hope it sends, so everything here takes `unknown` and checks it: anything
 * that isn't an exact moment with something to say is dropped rather than
 * guessed at — ranges and free text included.
 */
import type { ContentSeverity } from '../../types';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
/** `mm:ss` or `h:mm:ss`, and nothing else on either side. */
const TIMECODE_PATTERN = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/;
/** The severity word the AI puts in front of an inline risk flag. */
const SEVERITY_PATTERN = /^\s*(critical|high|medium|low)\b/i;
const DEFAULT_SEVERITY: ContentSeverity = 'High';
const AT_MARKER = '@';
const LABEL_SEPARATOR = ':';

export interface AiFlag {
  id: string;
  tMs: number;
  label: string;
  severity: ContentSeverity;
}

/** One inline `risk_flags[]` string, parsed. */
export interface InlineFlag {
  tMs: number;
  label: string;
  severity: ContentSeverity;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

/** A string with something in it — whitespace alone would render as an empty marker. */
function isFilledString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function listOf(value: unknown): unknown[] {
  if (!Array.isArray(value)) return [];
  return value;
}

/** `"MM:SS"` / `"H:MM:SS"` → milliseconds. Anything else — a non-string included → null. */
export function parseAiTimestamp(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const match = TIMECODE_PATTERN.exec(raw.trim());
  if (!match) return null;

  const hours = match[1] === undefined ? 0 : Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  // "0:99" and "1:75:00" are AI typos, not times — a real clock never overflows.
  if (seconds >= SECONDS_PER_MINUTE) return null;
  if (hours > 0 && minutes >= MINUTES_PER_HOUR) return null;

  const totalSeconds = (hours * MINUTES_PER_HOUR + minutes) * SECONDS_PER_MINUTE + seconds;
  return totalSeconds * MS_PER_SECOND;
}

function readSeverity(prefix: string): ContentSeverity {
  const match = SEVERITY_PATTERN.exec(prefix);
  if (!match?.[1]) return DEFAULT_SEVERITY;
  const word = match[1];
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Split the text after the `@` into its time token and the label that
 * follows. The token is whitespace-delimited, so a trailing `":"` (the
 * usual `"@ 00:01: label"` shape) is stripped before parsing, and a range
 * like `"0:54–1:00"` stays in one piece and fails to parse — as it should.
 */
function splitTimeAndLabel(after: string): { timeToken: string; label: string } {
  const trimmed = after.trimStart();
  const boundary = trimmed.search(/\s/);
  const rawToken = boundary === -1 ? trimmed : trimmed.slice(0, boundary);
  const rest = boundary === -1 ? '' : trimmed.slice(boundary);
  const timeToken = rawToken.endsWith(LABEL_SEPARATOR) ? rawToken.slice(0, -1) : rawToken;
  const label = rest.trim().replace(/^:\s*/, '');
  return { timeToken, label };
}

/** `"High @ 00:01: Missing FTC disclosure."` → its parts; no timed `@`, or not a string at all → null. */
export function parseInlineFlag(text: unknown): InlineFlag | null {
  if (typeof text !== 'string') return null;
  const at = text.indexOf(AT_MARKER);
  if (at === -1) return null;

  const { timeToken, label } = splitTimeAndLabel(text.slice(at + AT_MARKER.length));
  const tMs = parseAiTimestamp(timeToken);
  if (tMs === null) return null;
  // A moment with nothing to say about it would render as an empty marker.
  if (label === '') return null;

  return { tMs, label, severity: readSeverity(text.slice(0, at)) };
}

type UntaggedFlag = Omit<AiFlag, 'id'>;

/** The AI's severity word as it wrote it, or the default when it wrote none. */
function severityOf(value: unknown): ContentSeverity {
  if (!isFilledString(value)) return DEFAULT_SEVERITY;
  return value;
}

/** One `timestamped_issues[]` row, kept only with an exact moment and a label. */
function fromTimestampedIssue(row: unknown): UntaggedFlag | null {
  if (!isRecord(row) || !isFilledString(row.issue)) return null;
  const tMs = parseAiTimestamp(row.timestamp);
  if (tMs === null) return null;
  return { tMs, label: row.issue, severity: severityOf(row.severity) };
}

function isFlag(flag: UntaggedFlag | null): flag is UntaggedFlag {
  return flag !== null;
}

function fromTimestampedIssues(result: UnknownRecord): UntaggedFlag[] {
  return listOf(result.timestamped_issues).map(fromTimestampedIssue).filter(isFlag);
}

function fromRiskFlags(result: UnknownRecord): UntaggedFlag[] {
  return listOf(result.risk_flags).map(parseInlineFlag).filter(isFlag);
}

/** The same finding often appears as both an issue and a risk flag. */
function dedupe(flags: UntaggedFlag[]): UntaggedFlag[] {
  const seen = new Set<string>();
  const unique: UntaggedFlag[] = [];
  for (const flag of flags) {
    const key = `${flag.tMs}|${flag.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(flag);
  }
  return unique;
}

/** Every timed finding in an AI review result, deduped and in video order; nothing for anything that isn't one. */
export function extractAiFlags(result: unknown): AiFlag[] {
  if (!isRecord(result)) return [];
  const merged = dedupe([...fromTimestampedIssues(result), ...fromRiskFlags(result)]);
  merged.sort((a, b) => a.tMs - b.tMs);
  return merged.map((flag, index) => ({ ...flag, id: `ai:${flag.tMs}:${index}` }));
}
